import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Play, ArrowRight } from "lucide-react";
import PageShell from "@/components/PageShell";
import SectionRule from "@/components/SectionRule";
import BookCard from "@/components/BookCard";
import SongCard from "@/components/SongCard";
import TraditionTag from "@/components/TraditionTag";
import ScripturalAnchor from "@/components/ScripturalAnchor";
import AppShowcase from "@/components/AppShowcase";
import {
  getVisibleBooks,
  getFeaturedBook,
  getFeaturedSongs,
} from "@/lib/catalog";
import { featuredFilm } from "@/data/enrichments";
import { ytEmbedUrl, ytWatchUrl, ytThumbUrl } from "@/lib/youtube";

// Books-first homepage. No intro text or hero.
//
// Decision history (2026-05-02, Anant):
//   1. Dropped the Shiva-Tandava video hero ('its time is done').
//   2. Tried a compact text intro band — still ate ~1 mobile viewport
//      of vertical space before any product was visible.
//   3. Dropped the intro entirely. The brand line lives in the footer,
//      the about page, and the SEO meta. Books are the homepage.
//
// Same pattern as Apple's product homepages and modern SaaS storefronts.

function FeaturedFilm() {
  const [playing, setPlaying] = useState(false);
  const f = featuredFilm;
  const ytId = f.youtube_video_id;

  return (
    <section className="bg-parchment/40 section-y">
      <div className="max-w-8xl mx-auto px-5 md:px-10 lg:px-14">
        <SectionRule
          eyebrow="Now showing"
          title="The latest sacred film"
          lede="Long-form films on @dharmaweaveepics — meditation-paced, painterly, scripturally anchored."
        />

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
          <div className="lg:col-span-7">
            <div className="relative rounded-sm overflow-hidden card-shrine aspect-video bg-shrine-shadow">
              {playing ? (
                <iframe
                  src={ytEmbedUrl(ytId, { autoplay: 1 })}
                  title={f.title}
                  allow="autoplay; encrypted-media; picture-in-picture"
                  allowFullScreen
                  className="w-full h-full"
                />
              ) : (
                <button
                  onClick={() => setPlaying(true)}
                  className="absolute inset-0 group"
                  aria-label="Play"
                >
                  <img
                    src={ytThumbUrl(ytId, "maxresdefault")}
                    alt={f.title}
                    className="w-full h-full object-cover transition-transform duration-slower ease-breath group-hover:scale-[1.02]"
                    onError={(e) => {
                      e.currentTarget.src = ytThumbUrl(ytId, "hqdefault");
                    }}
                  />
                  <div className="absolute inset-0 bg-shrine-shadow/30 group-hover:bg-shrine-shadow/15 transition-colors duration-700 ease-breath" />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-20 h-20 md:w-24 md:h-24 rounded-full bg-cream/95 flex items-center justify-center shadow-xl group-hover:scale-105 transition-transform duration-700 ease-breath">
                      <Play
                        size={28}
                        className="text-ink ml-1"
                        fill="currentColor"
                      />
                    </div>
                  </div>
                </button>
              )}
            </div>
          </div>

          <div className="lg:col-span-5 lg:pl-4">
            <TraditionTag tradition="hindu" />
            <h3 className="font-display text-2xl md:text-4xl text-ink leading-tight mt-4">
              {f.title}
            </h3>
            <p className="font-serif italic text-ink-mute mt-2 text-base md:text-lg">
              {f.subtitle}
            </p>
            <p className="font-serif text-ink-soft text-lg md:text-xl mt-5 leading-relaxed">
              {f.description}
            </p>
            <div className="mt-6 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs font-body text-ink-mute uppercase tracking-widest2">
              <span>{f.duration}</span>
              <span className="text-ink-mute/40">·</span>
              <span>{f.channel}</span>
              <span className="text-ink-mute/40">·</span>
              <span>{new Date(f.published).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}</span>
            </div>
            <div className="mt-8 flex flex-wrap gap-3">
              <a
                href={ytWatchUrl(ytId)}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-pill btn-pill-solid"
              >
                Watch on YouTube
              </a>
              <Link to="/watch" className="btn-pill">
                All films
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function FeaturedBook() {
  const book = getFeaturedBook();
  if (!book) return null;
  return (
    <section className="bg-cream section-y">
      <div className="max-w-8xl mx-auto px-5 md:px-10 lg:px-14">
        <SectionRule
          eyebrow="Featured book"
          title={book.name}
          lede={book.description}
        />
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-5">
            <div className="card-shrine rounded-sm overflow-hidden aspect-[2/3] bg-parchment-deep relative">
              <img
                src={book.cover_url}
                alt={book.name}
                className="w-full h-full object-cover"
              />
            </div>
          </div>
          <div className="lg:col-span-7 lg:pl-6">
            <ScripturalAnchor anchor={book.anchor} />
            <Link
              to={`/books/${book.slug}`}
              className="inline-flex items-center gap-2 mt-8 btn-pill btn-pill-solid"
            >
              Read more about it
              <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

function ListenStrip() {
  const songs = getFeaturedSongs(6);
  return (
    <section className="bg-parchment/40 section-y">
      <div className="max-w-8xl mx-auto px-5 md:px-10 lg:px-14">
        <div className="flex items-end justify-between gap-6 mb-10 md:mb-14 flex-wrap">
          <SectionRule
            eyebrow="Music"
            title="Sit with a song"
            lede="Sanskrit, Pali, and Hindi devotional and contemplative tracks. Headphones or temple speakers — both work."
          />
          <Link
            to="/listen"
            className="font-body text-xs tracking-widest2 uppercase text-dharma-gold-deep hover:text-saffron-fire transition-colors mb-2"
          >
            Full catalogue →
          </Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-5 md:gap-6">
          {songs.map((c, i) => (
            <SongCard key={c.slug} card={c} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}

function ReadStrip() {
  const books = getVisibleBooks();
  return (
    <section className="bg-cream section-y">
      <div className="max-w-8xl mx-auto px-5 md:px-10 lg:px-14">
        <div className="flex items-end justify-between gap-6 mb-10 md:mb-14 flex-wrap">
          <SectionRule
            eyebrow="Books"
            title="Read the scripture as a graphic novel"
            lede="Each volume distils a passage from a living tradition into 60–120 painterly pages."
          />
          <Link
            to="/books"
            className="font-body text-xs tracking-widest2 uppercase text-dharma-gold-deep hover:text-saffron-fire transition-colors mb-2"
          >
            Every book →
          </Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-5 md:gap-7">
          {books.map((b, i) => (
            <BookCard key={b.slug} book={b} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <PageShell>
      <ReadStrip />
      <AppShowcase />
      <FeaturedBook />
      <ListenStrip />
      <FeaturedFilm />
    </PageShell>
  );
}
